import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Loader2, Save, X, Plus, Trash2 } from 'lucide-react';
import { planningService } from '../../services/planningService';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import SearchSelect from '../../components/SearchSelect';
import { PERIODS } from '../../utils/financialPeriods';
import usePeriodDrilldown, { periodColumns, periodsOfCol, periodHeaderToggle, aggPeriods } from '../../hooks/usePeriodDrilldown';

// =============================================================================
// Planning > returns lane (popup)
//
// Two twelve-period series, NOT a SKU plan: nobody budgets which SKU comes back
// off trade, they budget how many loads of returnable packaging and how much
// quality-reject volume. Both are already in the network capacity unit, so they
// convert straight to trips with no item-master lookup at all.
//
//   Returnable  pallets, crates, dollies going back for re-use
//   Quality     rejects and recalls coming back off trade
//
// A SKU CAN be attached, optionally, and that is the exception worth having: a
// recall or a known quality failure IS about one product, and a planner who
// knows which should be able to say so. Unattributed rows come first and always
// exist; product rows are added underneath. The lane total is the sum of both —
// a SKU row is a BREAKDOWN of nothing, it is its own line, so do not also enter
// it in the unattributed row or it counts twice.
// =============================================================================

const CATEGORY_LABEL = { RETURNABLE: 'Returnable packaging', QUALITY: 'Quality returns' };
const CATEGORY_HINT = {
  RETURNABLE: 'Pallets, crates and dollies coming back for re-use',
  QUALITY: 'Rejects and recalls coming back off trade',
};
const CATEGORIES = ['RETURNABLE', 'QUALITY'];

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const sum = (a) => a.reduce((x, v) => x + (Number(v) || 0), 0);

const T_LABEL = { bgCell: 'rgba(100, 116, 139, 0.08)', textDark: '#334155', baseFontStyle: '600 12px' };
const T_SKU = { bgCell: 'rgba(100, 116, 139, 0.04)', textDark: '#64748b' };
const T_TOTAL = { bgCell: 'rgba(100, 116, 139, 0.10)', baseFontStyle: '600 12px' };

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors';

export default function LaneReturnsModal({ laneId, readOnly, fyStartMonth, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [lane, setLane] = useState(null);
  const [rows, setRows] = useState([]);
  const [stockCodes, setStockCodes] = useState([]);
  const [capacityUom, setCapacityUom] = useState('PALLET');
  const [dirty, setDirty] = useState(false);

  const [adding, setAdding] = useState(false);
  const [newCat, setNewCat] = useState('QUALITY');
  const [newSku, setNewSku] = useState('');

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const res = await planningService.getLaneReturns(laneId);
        if (!live) return;
        const d = res.data || {};
        setLane(d.lane || null);
        setRows((d.rows || []).map((r) => ({ ...r, quantities: [...r.quantities] })));
        setStockCodes(d.stockCodes || []);
        setCapacityUom(d.capacityUom || 'PALLET');
        setDirty(false);
      } catch (e) {
        if (live) setError(e.response?.data?.message || e.message || 'Could not load the returns lane.');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [laneId]);

  const totals = useMemo(() => {
    const perPeriod = PERIODS.map((_, i) => rows.reduce((a, r) => a + (Number(r.quantities[i]) || 0), 0));
    return { perPeriod, year: sum(perPeriod) };
  }, [rows]);

  // Periods drill down — Full Year → quarters → months, same as OPEX entry.
  // Returns are quantities, so a group column SUMS its months.
  const drill = usePeriodDrilldown(PERIODS);

  const columns = useMemo(() => ([
    { id: 'category', title: 'Return type', width: 168 },
    { id: 'sku', title: 'Product (optional)', width: 190 },
    ...periodColumns(drill, fyStartMonth, { monthWidth: 72, groupWidth: 104 }),
    { id: 'year', title: 'Year', width: 92 },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [fyStartMonth, drill.visibleCols, drill.yearExpanded, drill.expandedQuarters]);

  const getCellContent = useCallback(([col, row]) => {
    if (row === rows.length) {
      if (col === 0) return gcell.text('TOTAL', { readonly: true, theme: T_TOTAL });
      if (col === 1) return gcell.text('', { readonly: true, theme: T_TOTAL });
      if (col === columns.length - 1) return gcell.number(totals.year, { readonly: true, display: fmt(totals.year), theme: T_TOTAL });
      const tps = periodsOfCol(columns, columns[col]?.id);
      if (!tps) return gcell.text('', { readonly: true, theme: T_TOTAL });
      const t = aggPeriods(tps, (p) => totals.perPeriod[p - 1]);
      return gcell.number(t, { readonly: true, display: fmt(t), theme: T_TOTAL });
    }
    const r = rows[row];
    if (!r) return gcell.text('');
    if (col === 0) return gcell.text(CATEGORY_LABEL[r.return_category] || r.return_category, { readonly: true, theme: T_LABEL });
    if (col === 1) {
      return gcell.text(
        r.stock_code ? `${r.stock_code}${r.stock_description ? ` · ${r.stock_description}` : ''}` : 'Not attributed',
        { readonly: true, theme: T_SKU }
      );
    }
    if (col === columns.length - 1) {
      const y = sum(r.quantities);
      return gcell.number(y, { readonly: true, display: fmt(y), theme: T_TOTAL });
    }
    // Addressed by ID, never by offset — the period columns fold now.
    const ps = periodsOfCol(columns, columns[col]?.id);
    if (!ps) return gcell.text('', { readonly: true });
    const v = aggPeriods(ps, (p) => r.quantities[p - 1]);
    return gcell.number(v, { readonly: readOnly || ps.length > 1, display: fmt(v), theme: ps.length > 1 ? T_TOTAL : undefined });
  }, [rows, totals, columns, readOnly]);

  const onCellEdited = useCallback(([col, row], newValue) => {
    if (readOnly || row >= rows.length) return;
    const ps = periodsOfCol(columns, columns[col]?.id);
    if (ps?.length !== 1) return; // months only
    const p = ps[0] - 1;
    const n = Number(newValue?.data);
    if (Number.isNaN(n) || n < 0) return;
    setRows((prev) => prev.map((r, i) => {
      if (i !== row) return r;
      const q = [...r.quantities];
      q[p] = n;
      return { ...r, quantities: q };
    }));
    setDirty(true);
  }, [rows.length, readOnly, columns]);

  const skuRows = rows.filter((r) => r.stock_code);

  const addSkuRow = () => {
    if (!newSku) return;
    const exists = rows.some((r) => r.return_category === newCat && r.stock_code === newSku);
    if (exists) { setError(`${newSku} is already listed under ${CATEGORY_LABEL[newCat]}.`); return; }
    const sc = stockCodes.find((s) => s.stock_code === newSku);
    setRows((prev) => ([...prev, {
      return_category: newCat,
      stock_code: newSku,
      stock_description: sc?.description || '',
      quantities: Array(12).fill(0),
    }]));
    setNewSku('');
    setAdding(false);
    setError('');
    setDirty(true);
  };

  const removeSkuRow = (cat, sku) => {
    setRows((prev) => prev.filter((r) => !(r.return_category === cat && r.stock_code === sku)));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await planningService.saveLaneReturns(
        laneId,
        rows.map((r) => ({
          return_category: r.return_category,
          stock_code: r.stock_code || null,
          quantities: r.quantities,
        }))
      );
      onSaved(res.message || 'Returns saved.');
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6" onClick={onClose}>
      <div className="w-full max-w-6xl max-h-[88vh] flex flex-col rounded-xl bg-white dark:bg-slate-800 shadow-2xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="shrink-0 flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="m-0 text-base font-bold text-slate-900 dark:text-slate-100 truncate">
                {lane ? `${lane.origin_name} → ${lane.dest_name}` : 'Returns lane'}
              </h2>
              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
                Returns
              </span>
            </div>
            <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Volume coming back, in {capacityUom.toLowerCase()}s. Attach a product only when the return really is about
              one — a recall, a known quality failure.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button className={btn} onClick={() => setAdding((a) => !a)} disabled={readOnly || busy}>
              <Plus className="w-3.5 h-3.5" /> Add product line
            </button>
            <button className={btnPrimary} onClick={save} disabled={readOnly || busy || !dirty}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
            </button>
            <button onClick={onClose} className="p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {error && (
          <div className="shrink-0 px-5 pt-3">
            <div className="flex items-start justify-between gap-2 px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10">
              <p className="m-0 text-xs text-red-700 dark:text-red-300">{error}</p>
              <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        )}

        {adding && (
          <div className="shrink-0 px-5 pt-3">
            <div className="flex flex-wrap items-end gap-2 px-3 py-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/40">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Return type</label>
                <select
                  className="text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2 py-1.5"
                  value={newCat}
                  onChange={(e) => setNewCat(e.target.value)}
                >
                  {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
                </select>
              </div>
              <div className="min-w-[240px]">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Product</label>
                <SearchSelect
                  value={newSku}
                  onChange={setNewSku}
                  options={stockCodes.map((s) => ({ code: s.stock_code, name: s.description }))}
                  placeholder="Search finished goods…"
                />
              </div>
              <button className={btnPrimary} onClick={addSkuRow} disabled={!newSku}>Add line</button>
              <button className={btn} onClick={() => { setAdding(false); setNewSku(''); }}>Cancel</button>
            </div>
          </div>
        )}

        <div className="flex-1 min-h-0 p-5 overflow-y-auto">
          {loading ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : (
            <>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                <MantleDataGrid
                  columns={columns}
                  rowCount={rows.length + 1}
                  getCellContent={getCellContent}
                  onCellEdited={onCellEdited}
                  onHeaderClicked={(col) => periodHeaderToggle(drill, columns[col]?.id)}
                  freezeColumns={2}
                  gridId="planning-lane-returns"
                  fitHeight
                  fitHeightPadding={8}
                  hint={`Click Full Year → quarters → months to drill into the periods. All figures are loads in ${capacityUom.toLowerCase()}s, so they become trips without touching the item master. A product line is its own line, not a breakdown of the unattributed row.`}
                />
              </div>

              {skuRows.length > 0 && (
                <div className="mt-3">
                  <p className="m-0 mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">Product lines</p>
                  <div className="flex flex-wrap gap-1.5">
                    {skuRows.map((r) => (
                      <span key={`${r.return_category}-${r.stock_code}`}
                            className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/40 text-slate-600 dark:text-slate-300">
                        <span className="font-semibold">{r.stock_code}</span>
                        <span className="text-slate-400">{CATEGORY_LABEL[r.return_category]}</span>
                        <button onClick={() => removeSkuRow(r.return_category, r.stock_code)}
                                disabled={readOnly}
                                className="p-0.5 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 disabled:opacity-40"
                                title="Remove this product line">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-3 grid grid-cols-2 gap-3">
                {CATEGORIES.map((c) => (
                  <div key={c} className="px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-900/50">
                    <p className="m-0 text-xs font-bold text-slate-700 dark:text-slate-200">{CATEGORY_LABEL[c]}</p>
                    <p className="m-0 mt-0.5 text-[11px] text-slate-400">{CATEGORY_HINT[c]}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
